import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppError } from '@/domain/ErrorApp.ts';
import { rawLogoff } from '@/shared/hook/usePostLogOff';
import { buildUrl } from './buildUrl';

interface Config {
  disableAutoFetch: boolean;
}

const defaultConfig: Config = {
  disableAutoFetch: false,
};

const removeUndefinedAttributes = (obj: Record<string, string>) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return Object.fromEntries(Object.entries(obj).filter(([_, value]) => value !== undefined));
};

export function useFetch<T>(url: string, params = {}, config = defaultConfig) {
  const cache: Record<string, unknown> = {};
  const [data, setData] = useState<T>();
  const [statusCode, setStatusCode] = useState<number>();
  const [error, setError] = useState<AppError | undefined>();
  const [loading, setLoading] = useState(true);
  const [refetchIndex, setRefetchIndex] = useState(0);

  const memoizedParams = useMemo(() => {
    return params;
  }, [JSON.stringify(params)]);

  const fetchData = useCallback(
    async (omitCache = false) => {
      try {
        const fullUrl = buildUrl(url, removeUndefinedAttributes(params));

        if (!omitCache && cache[fullUrl]) {
          setData(cache[fullUrl] as T);
          return;
        }

        setLoading(true);
        const response = await fetch(fullUrl, {
          credentials: 'include',
        });

        setStatusCode(response.status);
        if (response.status === 401 || response.status === 403) {
          await rawLogoff();
          return;
        }

        if (!response.ok) {
          let errorData = null;
          try {
            const text = await response.text(); // Intenta leer como texto primero
            errorData = text ? JSON.parse(text) : null;
          } catch (e) {
            console.warn('No se pudo parsear JSON de error', e);
          }

          setError(new AppError(errorData || { message: 'Error desconocido' }));
          return;
        }

        const responseData = await response.json();

        cache[fullUrl] = responseData;

        setData(responseData);
      } catch (error) {
        console.error(error);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        setError(new AppError(error));
      } finally {
        setLoading(false);
      }
    },
    [url, memoizedParams]
  );

  useEffect(() => {
    if (!config.disableAutoFetch) {
      fetchData();
    }
  }, [fetchData, refetchIndex]);

  const refetch = useCallback(
    (omitCache = false) => {
      setError(undefined);
      if (omitCache) {
        fetchData(omitCache);
      } else {
        setRefetchIndex((prev) => prev + 1);
      }
    },
    [fetchData]
  );

  return { data, setData, error, loading, refetch, statusCode, fetchData };
}
